import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart } from './_finHelpers';

// Social Security Calculator — Ideal application age + Compare two ages
export default function SocialSecurityCalc() {
  return (
    <CalcShell look="cnet" title="Social Security Calculator" subtitle="Determine the ideal age to claim Social Security and compare two application ages side-by-side."
      about={<><p><b>Social Security</b> retirement benefits can be claimed as early as age 62 or as late as age 70. Claiming earlier reduces monthly benefits; claiming later increases them. Break-even depends on life expectancy and investment return.</p></>}
    >
      <IdealAge/>
      <hr className="my-8 border-slate-200"/>
      <CompareAges/>
    </CalcShell>
  );
}

function IdealAge() {
  const [birthYear, setBirthYear] = useState(1970);
  const [lifeExp, setLifeExp] = useState(83);
  const [invReturn, setInvReturn] = useState(5);
  const [cola, setCola] = useState(3);
  const c = useMemo(() => {
    // Compute cumulative PV benefit for claiming at each age 62..70
    // Assume Full Retirement Age (FRA) = 67 for those born 1960+
    const FRA = 67;
    const rows = [];
    for (let claimAge = 62; claimAge <= 70; claimAge++) {
      // Reduction/increase factor
      let factor;
      if (claimAge < FRA) {
        // 5/9% per month for first 36 months, then 5/12% per month
        const monthsEarly = (FRA - claimAge) * 12;
        if (monthsEarly <= 36) factor = 1 - monthsEarly * 5/9/100;
        else factor = 1 - 36 * 5/9/100 - (monthsEarly - 36) * 5/12/100;
      } else if (claimAge > FRA) {
        // 8% per year delayed retirement credit
        factor = 1 + (claimAge - FRA) * 0.08;
      } else factor = 1;
      const monthlyBenefit = factor * 2000; // base assumption at FRA
      let pv = 0;
      let cur = monthlyBenefit;
      for (let y = 0; y < lifeExp - claimAge; y++) {
        pv += (cur * 12) / Math.pow(1 + invReturn/100, y);
        cur *= (1 + cola/100);
      }
      rows.push({ age: claimAge, factor, monthlyBenefit, pv });
    }
    const best = rows.reduce((a, b) => b.pv > a.pv ? b : a);
    return { rows, best };
  }, [birthYear, lifeExp, invReturn, cola]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Determine the ideal application age</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your birth year</label><input type="number" value={birthYear} onChange={e=>setBirthYear(+e.target.value||0)}/></div>
          <div className="field"><label>Your life expectancy</label><input type="number" value={lifeExp} onChange={e=>setLifeExp(+e.target.value||0)}/></div>
          <div className="field"><label>Your investment return</label><div className="flex gap-2"><input type="number" step="0.01" value={invReturn} onChange={e=>setInvReturn(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Cost of living adjustment*</label><div className="flex gap-2"><input type="number" step="0.01" value={cola} onChange={e=>setCola(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Ideal application age</div><div className="result-value" data-testid="ss-ideal">{c.best.age}</div></div>
          <table className="data-table"><thead><tr><th>Claim Age</th><th className="text-right">Monthly Benefit</th><th className="text-right">Lifetime PV</th></tr></thead>
            <tbody>
              {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${r.age===c.best.age?'bg-green-50 font-semibold':(i%2?'bg-slate-50':'')}`}><td className="px-2 py-1">{r.age}</td><td className="px-2 py-1 text-right">{fmt(r.monthlyBenefit)}</td><td className="px-2 py-1 text-right">{fmt(r.pv)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Lifetime Benefit by Claim Age</h3>
        <BarChart
          data={c.rows.map(r => ({
            label: String(r.age),
            values: [
              { value: r.monthlyBenefit, color: '#10b981' },
              { value: r.pv,             color: '#2563eb' },
            ]
          }))}
          stack={false}
          legend={[
            { label: 'Monthly benefit', color: '#10b981' },
            { label: 'Lifetime PV',      color: '#2563eb' },
          ]}
        />
      </div>
    </div>
  );
}

function CompareAges() {
  const [age1, setAge1] = useState(62);
  const [amt1, setAmt1] = useState(1600);
  const [age2, setAge2] = useState(70);
  const [amt2, setAmt2] = useState(2810);
  const [invReturn, setInvReturn] = useState(5);
  const [cola, setCola] = useState(3);
  const [lifeExp, setLifeExp] = useState(83);
  const c = useMemo(() => {
    const pv = (startAge, mo) => {
      let sum = 0; let cur = mo;
      for (let y = 0; y < lifeExp - startAge; y++) {
        sum += (cur * 12) / Math.pow(1 + invReturn/100, y);
        cur *= (1 + cola/100);
      }
      return sum;
    };
    const pv1 = pv(age1, amt1);
    const pv2 = pv(age2, amt2);
    // Break-even year
    let bal1 = 0, bal2 = 0, cur1 = amt1, cur2 = amt2;
    let breakEven = null;
    for (let a = age1; a <= 100; a++) {
      if (a >= age1) { bal1 += cur1 * 12; cur1 *= (1 + cola/100); }
      if (a >= age2) { bal2 += cur2 * 12; cur2 *= (1 + cola/100); }
      if (bal2 > bal1 && breakEven === null) breakEven = a;
    }
    return { pv1, pv2, better: pv2 > pv1 ? `Age ${age2}` : `Age ${age1}`, breakEven };
  }, [age1, amt1, age2, amt2, invReturn, cola, lifeExp]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-3">Compare two application ages</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Option 1</h4>
              <label className="text-xs">Retirement age</label><input type="number" value={age1} onChange={e=>setAge1(+e.target.value||0)}/>
              <label className="text-xs mt-2 block">Monthly payment</label><div className="flex gap-1"><span className="self-center">$</span><input type="number" value={amt1} onChange={e=>setAmt1(+e.target.value||0)} className="flex-1"/></div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Option 2</h4>
              <label className="text-xs">Retirement age</label><input type="number" value={age2} onChange={e=>setAge2(+e.target.value||0)}/>
              <label className="text-xs mt-2 block">Monthly payment</label><div className="flex gap-1"><span className="self-center">$</span><input type="number" value={amt2} onChange={e=>setAmt2(+e.target.value||0)} className="flex-1"/></div>
            </div>
          </div>
          <div className="field mt-3"><label>Your investment return</label><div className="flex gap-2"><input type="number" step="0.01" value={invReturn} onChange={e=>setInvReturn(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Cost of living adjustment*</label><div className="flex gap-2"><input type="number" step="0.01" value={cola} onChange={e=>setCola(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Life expectancy</label><input type="number" value={lifeExp} onChange={e=>setLifeExp(+e.target.value||0)}/></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Better choice</div><div className="result-value" data-testid="ss-compare">{c.better}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Option 1 lifetime PV</td><td className="text-right">{fmt(c.pv1)}</td></tr>
            <tr><td>Option 2 lifetime PV</td><td className="text-right">{fmt(c.pv2)}</td></tr>
            <tr><td>Break-even age</td><td className="text-right font-semibold">{c.breakEven || '—'}</td></tr>
          </tbody></table>
          <BarChart
            data={[
              { label: `Age ${age1}`, values: [{ value: c.pv1, color: '#2563eb' }] },
              { label: `Age ${age2}`, values: [{ value: c.pv2, color: '#10b981' }] },
            ]}
            stack={false}
            legend={[
              { label: `Option 1 (age ${age1})`, color: '#2563eb' },
              { label: `Option 2 (age ${age2})`, color: '#10b981' },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
